<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {Button} from "@/core/components/ui/button";
import {Label} from "@/core/components/ui/label";
import AgendaSlotGrid from "@/agenda/components/AgendaSlotGrid.vue";
import AgendaPatientSmsOption from "@/agenda/components/AgendaPatientSmsOption.vue";
import useAppointmentTypes from "@/agenda/composables/useAppointmentTypes";
import useOperatorChoices from "@/agenda/composables/useOperatorChoices";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {DEFAULT_APPOINTMENT_COLOR} from "@/agenda/constants";
import {
    emptyPatientSmsChoice,
    isPatientSmsChoiceValid,
    PatientSmsChoice,
    patientSmsOutcome,
    patientSmsRequest
} from "@/agenda/lib/patientSms";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {appointmentApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {apiErrorMessage, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {useToastStore} from "@/stores/toast.store";
import {useSystemStore} from "@/stores/system.store";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";

/**
 * Book the appointment of a step of the review, or move the one booked.
 *
 * The appointment is an agenda appointment like any other, linked to the
 * step it plans so the review reads it back: the agenda stays the one place
 * it lives, moved or canceled there as well as here.
 *
 * Booking opens on the suggested day, the pharmaceutical interview type and
 * the member holding the session; the pharmacist changes any of them. Moving
 * changes the time only, as the agenda does when a card is dropped
 * elsewhere: the type and the member stay those of the appointment.
 */
const props = defineProps<{
    /** The step the appointment plans. */
    plannedStepId: number;
    /** The label of that step, as the definition declares it. */
    plannedStepLabel: string;
    /** The day suggested for it, `YYYY-MM-DD`. */
    suggestedOn: string;
    /** The appointment moved, null to book one. */
    appointment: AgendaAppointment | null;
}>();

const emits = defineEmits<{(e: 'saved'): void}>();

const open = defineModel<boolean>('open', {required: true});

/**
 * The type a booking lands on: the review is a pharmaceutical interview.
 * A pharmacy that hid it lands on its first type instead.
 */
const DEFAULT_TYPE_SLUG = 'pharmaceutical_interview';

/** What an appointment lasts when no type says otherwise. */
const DEFAULT_DURATION = 30;

const store = useMedicationCheckupStore();
const toastStore = useToastStore();
const organizationId = useOrganizationId();
const {environment} = storeToRefs(useSystemStore());
const {data: types} = useAppointmentTypes();

const moving = computed(() => props.appointment !== null);

const typeId = ref<number | null>(null);
const day = ref(props.suggestedOn);
const memberId = ref<number | null>(null);
const picked = ref<{date: string; start: string; member_id: number | null} | null>(null);
const sms = ref<PatientSmsChoice>(emptyPatientSmsChoice());
const saving = ref(false);

const selectedType = computed(() => types.value?.find(type => type.id === typeId.value) ?? null);

const {choices, preferred} = useOperatorChoices(() => ({appointmentTypeId: typeId.value}));

const duration = computed(() => {
    if (props.appointment) {
        return parseAgendaDate(props.appointment.end_at).diff(parseAgendaDate(props.appointment.start_at), 'minute');
    }

    return selectedType.value?.duration ?? DEFAULT_DURATION;
});

const defaultTypeId = (): number | null => {
    const all = types.value ?? [];

    return all.find(type => type.slug === DEFAULT_TYPE_SLUG)?.id ?? all[0]?.id ?? null;
};

/**
 * The member holding the session when the team counts them, whatever their
 * rank: they just held the interview. Else the one the agenda would pick.
 */
const defaultMemberId = (): number | null => {
    const session = environment.value?.id ?? null;

    return choices.value.some(choice => choice.operator.accessId === session) ? session : preferred(session);
};

watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    const appointment = props.appointment;

    typeId.value = appointment ? appointment.appointment_type?.id ?? null : defaultTypeId();
    day.value = appointment ? parseAgendaDate(appointment.start_at).format('YYYY-MM-DD') : props.suggestedOn;
    memberId.value = appointment ? appointment.operator_access_point_id : defaultMemberId();
    picked.value = null;
    sms.value = emptyPatientSmsChoice();
}, {immediate: true});

// The types and the team load on their own: a booking opened before them
// lands on its defaults once they are there.
watch(types, () => {
    if (open.value && !moving.value && typeId.value === null) {
        typeId.value = defaultTypeId();
    }
});

watch(choices, () => {
    if (open.value && !moving.value && memberId.value === null && picked.value === null) {
        memberId.value = defaultMemberId();
    }
});

// A slot is chosen for a day, a member and a length: changing any of them
// asks for the slot again.
watch([day, memberId, typeId], () => {
    picked.value = null;
});

const selectType = (value: unknown) => {
    typeId.value = value ? Number(value) : null;
};

const startAt = computed(() => picked.value
    ? parseAgendaDate(`${picked.value.date}T${picked.value.start}`).format()
    : null);

const pickedLabel = computed(() => picked.value
    ? parseAgendaDate(`${picked.value.date}T${picked.value.start}`).format('dddd D MMMM [à] HH:mm')
    : null);

const canSubmit = computed(() => picked.value !== null
    && !saving.value
    && isPatientSmsChoiceValid(sms.value)
    && (moving.value || store.customerId !== null));

const book = (start: string) => {
    return appointmentApi().createAppointment(organizationId.value as number, {
        customer_id: store.customerId,
        ...(typeId.value ? {appointment_type_id: typeId.value} : {}),
        operator_access_point_id: picked.value?.member_id ?? null,
        start_at: start,
        duration: duration.value,
        title: `Bilan partagé de médication · ${props.plannedStepLabel}`,
        description: `${props.plannedStepLabel} · Année ${store.cycle?.rank ?? 1}`,
        appointmentable_type: 'medication_checkup_step',
        appointmentable_id: props.plannedStepId,
        ...patientSmsRequest(sms.value)
    });
};

const submit = async () => {
    const start = startAt.value;

    if (!canSubmit.value || start === null || !organizationId.value) {
        return;
    }

    saving.value = true;

    try {
        const {patientNotification} = props.appointment
            ? await appointmentApi().rescheduleAppointment(props.appointment.id, {
                start_at: start,
                ...patientSmsRequest(sms.value)
            })
            : await book(start);

        toastStore.showSuccess({
            content: moving.value
                ? `Rendez-vous déplacé au ${pickedLabel.value}.`
                : `Rendez-vous pris le ${pickedLabel.value}.`
        });

        const outcome = patientSmsOutcome(sms.value, patientNotification);

        if (outcome) {
            toastStore.showToast(outcome);
        }

        emits('saved');
        open.value = false;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        saving.value = false;
    }
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
      <DialogHeader>
        <DialogTitle>
          {{ moving ? 'Déplacer le rendez-vous' : 'Prévoir le rendez-vous' }} — {{ plannedStepLabel }}
        </DialogTitle>
        <DialogDescription>
          {{ duration }} minutes{{ moving ? '' : `, suggéré vers le ${parseAgendaDate(suggestedOn).format('dddd D MMMM')}` }}.
        </DialogDescription>
      </DialogHeader>

      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        <div v-if="!moving" class="flex flex-col gap-1.5">
          <Label>Type de rendez-vous</Label>
          <Select
            :model-value="typeId ? String(typeId) : undefined"
            @update:model-value="selectType"
          >
            <!--
              reka's SelectValue cannot resolve the label of a value set
              before the options were ever mounted (the content lives in a
              closed portal), so the selected label is rendered explicitly.
            -->
            <SelectTrigger class="h-9 rounded-lg focus:border-primary focus:ring-primary">
              <span v-if="selectedType" class="flex min-w-0 flex-1 items-center gap-2">
                <span
                  class="size-2.5 shrink-0 rounded-full"
                  :style="{backgroundColor: selectedType.color ?? DEFAULT_APPOINTMENT_COLOR}"
                />
                <span class="truncate">{{ selectedType.name }}</span>
                <span class="ml-auto pr-1 text-xs tabular-nums text-gray-500">{{ selectedType.duration }} min</span>
              </span>
              <SelectValue v-else placeholder="Choisir un type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="type in types ?? []"
                :key="type.id"
                :value="String(type.id)"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="size-2.5 shrink-0 rounded-full"
                    :style="{backgroundColor: type.color ?? DEFAULT_APPOINTMENT_COLOR}"
                  />
                  {{ type.name }}
                  <span class="text-xs tabular-nums text-gray-500">{{ type.duration }} min</span>
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label>Créneau</Label>
          <AgendaSlotGrid
            v-model:day="day"
            v-model:member="memberId"
            :appointment-type-id="typeId"
            :duration-minutes="duration"
            :active="open"
            :selected="picked?.date === day ? picked.start : null"
            :excluded-appointment-id="appointment?.id ?? null"
            :member-locked="moving"
            @pick="picked = $event"
          />
        </div>

        <AgendaPatientSmsOption
          v-if="picked && appointment"
          v-model="sms"
          :appointment-id="appointment.id"
          :start-at="startAt"
          event="rescheduled"
        />
        <AgendaPatientSmsOption
          v-else-if="picked && store.customerId !== null"
          v-model="sms"
          :customer-id="store.customerId"
          :start-at="startAt"
          event="created"
        />
      </div>

      <DialogFooter class="items-center gap-2 sm:justify-between">
        <span class="text-sm text-muted-foreground">
          {{ pickedLabel ? `Le ${pickedLabel}` : 'Choisissez un créneau.' }}
        </span>
        <Button :disabled="!canSubmit" class="bg-primary hover:bg-primary/90" @click="submit">
          {{ moving ? 'Déplacer le rendez-vous' : 'Prendre le rendez-vous' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
